import {type BucketedHistoryResponse} from '../../../../generated/historyapi/history_pb';
import {
  recordRumDurationVital,
  recordRumError,
} from '../../../../util/datadog/datadogRum';
import {historyClient} from '../../../../util/historyConnect';
import {type BucketedLine} from '../../types';
import {type QueryVars} from '../queries/queryVars';
import {getLineGroup, trackedBucketedDataEvent} from '../queries/utils';

// Fetches bucketed history over Connect, one Arrow sketch per run per spec.
export function fetchHistoryColumnar(
  vars: QueryVars,
  currentRuns: string[],
  lastUpdated: string | undefined
): Promise<BucketedHistoryResponse> {
  return historyClient.getBucketedHistory({
    entity: vars.entityName,
    project: vars.projectName,
    runFilter: vars.filters,
    specs: vars.bucketedHistorySpecs,
    limit: vars.limit,
    order: vars.order,
    currentRuns,
    lastUpdated,
  });
}

const EPOCH_ISO = new Date(0).toISOString();

export async function columnarBucketedQuery(
  vars: QueryVars,
  runIds: string[],
  lastUpdated: string,
  currentRunsById: Record<string, BucketedLine>
): Promise<{
  incomplete: string[];
  retryAfterSeconds: number | undefined;
  isAggregated: boolean;
  order: string[];
  runsById: Record<string, BucketedLine>;
}> {
  const isInitialQuery = runIds.length === 0;
  // Epoch-anchored so the whole fetch + decode can be reported as a duration vital.
  const startTime = performance.timeOrigin + performance.now();
  const fetchStart = performance.now();
  let response: BucketedHistoryResponse;
  try {
    response = await fetchHistoryColumnar(
      vars,
      runIds,
      isInitialQuery ? undefined : lastUpdated
    );
  } catch (e) {
    // Whole-query fetch failure, distinct from a per-run decode failure.
    recordRumError(e, {
      source: 'bucketed_columnar_fetch',
      entity: vars.entityName,
      project: vars.projectName,
    });
    throw e;
  }
  const fetchMs = performance.now() - fetchStart;

  // Decode phase includes the one-time dynamic import of the Arrow decoder.
  const decodeStart = performance.now();
  const {decodeSketchArrow} = await import('./decodeSketchArrow');

  const decodedById: Record<string, BucketedLine> = {};
  const incomplete: string[] = [];
  let specCount = 0;
  let wireBytes = 0;
  let skippedRunCount = 0;
  for (const run of response.runs) {
    if (run.incomplete) {
      incomplete.push(run.runName);
      continue;
    }
    // Single pass: tally wire bytes and decode non-empty payloads together.
    let historyColumnar: BucketedLine['historyColumnar'];
    try {
      const cols: BucketedLine['historyColumnar'] = [];
      for (const bytes of run.specsArrow) {
        specCount++;
        wireBytes += bytes.length;
        if (bytes.length > 0) {
          cols.push(decodeSketchArrow(bytes));
        }
      }
      historyColumnar = cols;
    } catch (e) {
      // A malformed Arrow payload skips just this run, not the whole batch.
      skippedRunCount++;
      console.error(
        `Error decoding columnar bucketed history for run ${run.runName}`,
        e
      );
      recordRumError(e, {
        source: 'bucketed_columnar_decode',
        runName: run.runName,
        entity: vars.entityName,
        project: vars.projectName,
      });
      continue;
    }
    const updatedAt = run.updatedAt
      ? new Date(run.updatedAt).toISOString()
      : EPOCH_ISO;
    decodedById[run.runName] = {
      id: run.runName,
      name: run.runName,
      displayName: run.displayName || run.runName,
      createdAt: EPOCH_ISO,
      updatedAt,
      heartbeatAt: EPOCH_ISO,
      group: null,
      host: null,
      jobType: null,
      state: '',
      stopped: null,
      user: {name: '', photoUrl: ''},
      _wandb: {},
      config: {},
      summary: {},
      aggregations: {min: {}, max: {}},
      tags: [],
      history: [],
      historyColumnar,
    };
  }

  const order =
    response.order && response.order.length > 0
      ? response.order
      : Object.keys(decodedById);
  const incompleteSet = new Set(incomplete);
  const runsById: Record<string, BucketedLine> = {};
  for (const name of order) {
    if (incompleteSet.has(name)) {
      continue;
    }
    const merged = decodedById[name] ?? currentRunsById[name];
    if (merged != null) {
      runsById[name] = merged;
    }
  }

  const decodeMs = performance.now() - decodeStart;
  const runCount = response.runs.length;
  const numMetrics = vars.bucketedHistorySpecs.length;
  const loadedRunCount = Object.keys(runsById).length;
  trackedBucketedDataEvent('Columnar_query_complete', {
    runCount,
    decodedRunCount: loadedRunCount,
    skippedRunCount,
    specCount,
    wireBytes,
  });

  // Emit the same vital as the record path so columnar panels land in existing
  // LinePlotQueryProfile dashboards; extra columnar-only fields ride in context.
  recordRumDurationVital('LinePlotQueryProfile', {
    description: 'Measures the performance of the line plot query',
    startTime,
    duration: performance.timeOrigin + performance.now() - startTime,
    context: {
      behavior: 'BucketedColumnarQuery',
      bins: vars.bins,
      isInitialQuery,
      isMultiMetrics: numMetrics > 1,
      isSystemMetrics: vars.isSystemMetrics,
      isThrottledMultiMetrics: false,
      groupKeys: vars.groupKeys,
      hasIncomplete: incomplete.length > 0,
      incompleteCount: incomplete.length,
      internalId: vars.internalId,
      maxGroups: vars.maxGroups,
      numMetrics,
      loadedRunCount,
      numSpecs: numMetrics,
      order: vars.order,
      panelType: vars.panelType,
      project: vars.projectName,
      totalLines: loadedRunCount * numMetrics,
      lineGroup: getLineGroup(loadedRunCount * numMetrics),
      skippedRunCount,
      specCount,
      wireBytes,
      fetchMs,
      decodeMs,
    },
  });

  return {
    incomplete,
    retryAfterSeconds: undefined,
    isAggregated: false,
    order,
    runsById,
  };
}
